import type { Client, RequestContext } from "../client";
import { Repository } from "../repository";
import type {
    ProcessTemplateSummary,
    ProcessTemplateResource,
    DeleteProcessTemplateCommand,
    ModifyProcessTemplateCommand,
    GitRef,
    NewProcessTemplate,
    CreateProcessTemplateVersionCommand,
    ProcessTemplateVersionResource,
    ShareProcessTemplateCommand,
    ShareProcessTemplateResponse,
    GetProcessTemplateSharingConfigurationResponse,
    ProcessTemplateVersionSummary,
    GitLinkResult,
} from "../resources";

export class ProcessTemplateRepository {
    protected readonly client: Client;
    protected readonly context: RequestContext;

    protected apiBaseRoute: string = "~/api/platformhub";

    constructor(client: Client, requestContext: RequestContext) {
        this.client = client;
        this.context = requestContext;
    }

    get(slug: string, gitRef: GitRef) {
        return this.client.get<ProcessTemplateResource>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/{slug}`, { slug, gitRef });
    }

    getByVersionMask(slug: string, versionMask: string) {
        return this.client.get<GetProcessTemplateByVersionMaskResponse>(this.context, "~/api/spaces/{spaceId}/processtemplates/{slug}/{versionMask}", { slug, versionMask });
    }

    getByVersionMaskFromSystem(slug: string, versionMask: string) {
        return this.client.get<GetProcessTemplateByVersionMaskResponse>(this.context, "~/api/platformhub/processtemplates/{slug}/{versionMask}", { slug, versionMask });
    }

    getVersionSummary(slug: string) {
        return this.client.get<ProcessTemplateVersionSummary>(this.context, "~/bff/spaces/{spaceId}/processtemplates/{slug}/versionsummary", { slug });
    }

    getVersionSummaryFromSystem(slug: string) {
        return this.client.get<ProcessTemplateVersionSummary>(this.context, "~/bff/platformhub/processtemplates/{slug}/versionsummary", { slug });
    }

    del(deleteProcessTemplateCommand: DeleteProcessTemplateCommand) {
        return this.client.del(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/{slug}`, deleteProcessTemplateCommand, { gitRef: deleteProcessTemplateCommand.GitRef, slug: deleteProcessTemplateCommand.ProcessTemplateSlug });
    }

    create(processTemplate: NewProcessTemplate, gitRef: GitRef) {
        return this.client.post<ProcessTemplateResource>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates`, processTemplate, { gitRef });
    }

    modify(processTemplate: ModifyProcessTemplateCommand) {
        return this.client.put<ProcessTemplateResource>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/{slug}`, processTemplate, { gitRef: processTemplate.GitRef, slug: processTemplate.Slug });
    }

    getSummaries(processTemplatesPageFilter: GetProcessTemplateSummariesRequest): Promise<GetProcessTemplateSummariesResponse> {
        return this.client.get<GetProcessTemplateSummariesResponse>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/summaries{?skip,take,partialName}`, processTemplatesPageFilter);
    }

    createVersion(createVersionCommand: CreateProcessTemplateVersionCommand) {
        return this.client.post<ProcessTemplateVersionResource>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/{slug}/versions`, createVersionCommand, {
            slug: createVersionCommand.ProcessTemplateSlug,
            gitRef: createVersionCommand.GitRef,
        });
    }

    getVersions(slug: string) {
        return this.client.get<ProcessTemplateVersionResource[]>(this.context, `${this.apiBaseRoute}/processtemplates/{slug}/versions{?skip,take}`, { slug, skip: 0, take: Repository.takeAll });
    }

    share(shareCommand: ShareProcessTemplateCommand) {
        return this.client.post<ShareProcessTemplateResponse>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/{slug}/share`, shareCommand, { slug: shareCommand.ProcessTemplateSlug, gitRef: shareCommand.GitRef });
    }

    getSharingConfiguration(slug: string) {
        return this.client.get<GetProcessTemplateSharingConfigurationResponse>(this.context, `${this.apiBaseRoute}/processtemplates/{slug}/share`, { slug });
    }

    getVariableNames(slug: string, gitRef: string | undefined) {
        return this.client.get<string[]>(this.context, `${this.apiBaseRoute}/{gitRef}/processtemplates/{slug}/variables/names`, { slug, gitRef });
    }

    getLink(slug: string, gitRef: GitRef) {
        return this.client.get<GitLinkResult>(this.context, "~/bff/platformhub/{gitRef}/processtemplates/{slug}/link", { slug, gitRef });
    }
}

export type GetProcessTemplateSummariesRequest = {
    skip: number;
    take: number;
    partialName?: string;
    gitRef: string;
};

export interface GetProcessTemplateSummariesResponse {
    ProcessTemplateSummaries: ProcessTemplateSummary[];
    TotalResults: number;
    ItemsPerPage: number;
    TotalNoOfProcessTemplates: number;
}

export interface GetProcessTemplateByVersionMaskResponse {
    ProcessTemplate: ProcessTemplateResource;
    ProcessTemplateVersion: string;
}
